import Button from 'components/Atoms/Button';
import Text from 'components/Atoms/Text';
import TextField from 'components/Atoms/TextField';
import { loginSchema } from 'pages/login';
import { yupResolver } from '@hookform/resolvers/yup';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { CircularProgress, IconButton, InputAdornment } from '@mui/material';
import { Auth } from 'aws-amplify';
import { useHistory } from "react-router-dom";
import React, { useEffect, useState } from 'react'
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { authActions } from 'pages/login/auth.slice';
import { TLoginForm } from 'pages/login/types';
import { useAppDispatch, useTypedSelector } from 'store/store';
import Link from 'components/Atoms/Link';
import storage from 'helpers/storage';
import { triggerUxCritialErrors } from 'actions/error';
import { processLogin } from 'components/shared/LoginHelper';
import clsx from 'clsx';

const LoginSection = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();

  const app = useTypedSelector(state => state.app);

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    handleSubmit,
    formState: { errors },
    control
  } = useForm<TLoginForm>({
    resolver: yupResolver(loginSchema),
  });

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const onSubmit: SubmitHandler<TLoginForm> = async (data) => {
    setIsLoading(true);

    Auth.signIn(data.username, data.password).then((user) => {
      if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
        console.log('new password');
        dispatch(authActions.actions.setCognitoUser(user));

        setIsLoading(false);

        history.push('/new-password');
      } else {
        Auth.userSession(user).then(session => {
          const idToken = session.getIdToken().getJwtToken();
          const tokensPayload = { access: idToken };
          const _user: any = session.getIdToken().payload.user_details;
          const user = JSON.parse(_user);

          storage.set("authToken", idToken, "local");
          storage.set("showReleaseNotes", true, "local");
          storage.set("authFailed", false, "local");

          dispatch({
            payload: {
              authorizationDone: true,
            },
            type: "AUTHORIZATION_DONE",
          });

          dispatch({
            payload: {
              userDetails: user
            },
            type: "USER_DETAILS_FETCHED",
          });

          dispatch(authActions.actions.setTokens(tokensPayload));
          dispatch(authActions.actions.setUser(user));

          processLogin(session, 'cognito', dispatch);

          setIsLoading(false);

          history.replace('/');
        });
      }
    }, failed => {
      setIsLoading(false);
      triggerUxCritialErrors(failed, dispatch);
    });
  };

  useEffect(() => {
    if (app.userAuthenticated) {
      history.replace('/');
    }
  }, [app, history]);

  return (
    <div className="rounded-lg py-6 px-12 flex flex-col gap-y-4 w-full 2xl:max-w-2xl xl:m-auto">
      <div className='pb-3'>
        <Text variant="body1" className='text-red-950 font-normal text-2xl md:text-4xl lg:text-4xl text-capitalize'>Login</Text>
      </div>
      <form className='flex flex-col gap-y-5' onSubmit={handleSubmit(onSubmit)}>
        <Controller name="username" control={control} render={({ field }) => <TextField
          {...field}
          error={Boolean(errors.username)}
          fullWidth
          placeholder="Enter User ID"
          label="User ID"
          helperText={errors.username?.message}
          InputProps={{ className: 'bg-gray-850' }}
          InputLabelProps={{
            className: clsx([{ 'font-extrabold': field.value }])
          }}
        />} />
        <Controller name="password" control={control} render={({ field }) => <TextField
          {...field}
          error={Boolean(errors.password)}
          helperText={errors.password?.message}
          fullWidth
          placeholder="Enter Password"
          label="Password"
          type={showPassword ? "text" : "password"}
          InputProps={{
            className: 'bg-gray-850',
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          InputLabelProps={{
            className: clsx([{ 'font-extrabold': field.value }])
          }}
        />} />
        <Button
          type='submit'
          color="primary"
          variant="contained"
          size="large"
          startIcon={isLoading && <CircularProgress color="info" size={24} />}
          disabled={isLoading}
        >
          {` ${isLoading ? 'Loading...' : 'Sign In'}`}
        </Button>
      </form>
      <div className="flex items-center justify-center">
        <div>
          <Link className="underline text-black font-semibold text-lg" href="/forgot-password">
            Forgot Password?
          </Link>
        </div>
      </div>
    </div >
  )
}

export default LoginSection;
